import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import type { Analysis } from '../../types/models';
import { ApiError } from '../../lib/errors';
import { AnalysisCard } from './AnalysisCard';
import { AnalysisDetails } from './AnalysisDetails';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Search, Filter, Terminal, Info } from 'lucide-react';

const DATE_FILTERS = [
  { label: 'All Time', value: 'all' },
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'Last 90 Days', value: '90days' }
];

interface AnalysisWithRelations extends Analysis {
  candidates?: { name: string } | null;
  jobs?: { title: string } | null;
}

export function AnalysisHistory() {
  const [selectedAnalysis, setSelectedAnalysis] = useState<string | null>(null);
  const [userAnalyses, setUserAnalyses] = useState<AnalysisWithRelations[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [isFetching, setIsFetching] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const getCurrentUserAnalyses = async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new ApiError('User not authenticated', 401);

      const { data, error } = await supabase
        .from('analysis')
        .select('*, candidates(name), jobs(title)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserAnalyses((data as AnalysisWithRelations[]) || []);
    } catch (err: any) {
      console.error('Error fetching user analyses:', err);
      setFetchError(err.message || 'Failed to load analysis history.');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    getCurrentUserAnalyses();
  }, []);

  const filteredAnalyses = useMemo(() => {
    return userAnalyses.filter(analysis => {
      const searchTerm = searchQuery.toLowerCase();
      const candidateName = analysis.candidates?.name?.toLowerCase() || '';
      const jobTitle = analysis.jobs?.title?.toLowerCase() || '';
      const matchesSearch = !searchQuery || 
        candidateName.includes(searchTerm) || 
        jobTitle.includes(searchTerm);

      if (!analysis.created_at) return matchesSearch;

      const analysisDate = new Date(analysis.created_at);
      if (isNaN(analysisDate.getTime())) return matchesSearch;

      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - analysisDate.getTime()) / (1000 * 60 * 60 * 24));

      let matchesDate = true;
      if (dateFilter === '7days') matchesDate = daysDiff <= 7;
      if (dateFilter === '30days') matchesDate = daysDiff <= 30;
      if (dateFilter === '90days') matchesDate = daysDiff <= 90;

      return matchesSearch && matchesDate;
    });
  }, [userAnalyses, searchQuery, dateFilter]);

  if (isFetching && userAnalyses.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-full sm:w-[200px]" />
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <Alert variant="destructive">
        <Terminal className="h-4 w-4" />
        <AlertTitle>Error Loading History</AlertTitle>
        <AlertDescription>{fetchError}</AlertDescription>
      </Alert>
    );
  }

  if (selectedAnalysis) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedAnalysis(null)}
          className="flex items-center gap-2 text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to History
        </Button>
        <AnalysisDetails
          analysisId={selectedAnalysis}
          onClose={() => setSelectedAnalysis(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
         <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
               <div className="relative flex-1">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
                 <Input
                   type="text"
                   placeholder="Search by candidate or position..."
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   className="w-full pl-9"
                 />
               </div>
               
               <div className="flex items-center gap-2 min-w-[180px]">
                  <Filter className="h-4 w-4 text-primary flex-shrink-0" />
                  <Select value={dateFilter} onValueChange={setDateFilter}>
                     <SelectTrigger className="w-full">
                       <SelectValue placeholder="Select date range" />
                     </SelectTrigger>
                     <SelectContent>
                       {DATE_FILTERS.map(filter => (
                         <SelectItem key={filter.value} value={filter.value}>
                           {filter.label}
                         </SelectItem>
                       ))}
                     </SelectContent>
                   </Select>
               </div>
            </div>
         </CardContent>
       </Card>

      {filteredAnalyses.length === 0 ? (
         <Card className="flex flex-col items-center justify-center min-h-[200px] text-center p-6 bg-muted/10">
            <Info className="w-10 h-10 text-primary/70 mb-3" />
            <p className="text-sm font-medium text-foreground">No matching analyses found.</p>
            <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or filter criteria.</p>
         </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredAnalyses.map((analysis) => (
            <AnalysisCard
              key={analysis.id}
              analysis={analysis}
              onClick={() => setSelectedAnalysis(analysis.id ?? null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}